import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  GraphQueryTemplate,
  GraphQueryTemplateAttributes,
  GraphQueryTemplateId,
  GraphQueryTemplateStatus,
} from 'dashboard/models/graph-query-template';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/graph-query-templates';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  archiveGraphQueryTemplate,
  loadGraphQueryTemplates,
  restoreGraphQueryTemplate,
} from 'dashboard/redux/thunk-actions/graph-query-templates';
import DashboardGraphQueryTemplatesView from 'dashboard/views/DashboardGraphQueryTemplatesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardGraphQueryTemplatesRoute = ({
  currentEnvironment,
  currentPage,
  filters,
  graphQueryTemplates,
  pageSize,
  paginationParams,
  sortParams,
  archiveGraphQueryTemplate,
  loadGraphQueryTemplates,
  restoreGraphQueryTemplate,
  setFilters,
  setSortParams,
  setCurrentPage,
  setPageSize,
  showMessage,
}: PropsFromRedux) => {
  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    sortParams,
    pageSize,
    currentPage,
    setSortParams,
    setCurrentPage,
    setPageSize,
  });

  // ----------------------------------------------------------------------------------------
  // Loading graph templates.
  // ----------------------------------------------------------------------------------------

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadGraphQueryTemplates, currentEnvironment, currentPage, pageSize, filters],
  );

  const { loading: _loadingTemplates, callback: _graphQueryTemplatesLoader } = useApiLoader({
    loader: () =>
      loadGraphQueryTemplates(
        filters,
        sortParams || { sort: 'name', shouldSortDescending: false },
        abortController.signal,
      ),
    deps: [filters, sortParams],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load graph query templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // ----------------------------------------------------------------------------------------
  // Creating new graph templates.
  // ----------------------------------------------------------------------------------------

  const { error: createGraphQueryTemplateError, callback: _createTemplate } = useApiCallback(
    async (name: string) => {
      await SessionClientDashboard.postResourceCreate<GraphQueryTemplate>({
        type: ApiModel.GraphQueryTemplate,
        attributes: {
          'environment-id': currentEnvironment.id,
          name,
        },
      });
      const displayMessage = `Successfully created graph query template with name ${name}`;
      showMessage(displayMessage, MessageType.Success);
    },
    [currentEnvironment.id],
  );

  useApiErrorHandler({
    error: createGraphQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to create graph query template, please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // ----------------------------------------------------------------------------------------
  // Updating graph templates.
  // ----------------------------------------------------------------------------------------

  const { error: updateGraphQueryTemplateError, callback: _updateTemplate } = useApiCallback(
    async (id: GraphQueryTemplateId, params: Partial<GraphQueryTemplateAttributes>) => {
      await SessionClientDashboard.updateResource<GraphQueryTemplate>({
        type: ApiModel.GraphQueryTemplate,
        id,
        attributes: {
          ...params,
        },
      });
    },
    [],
  );

  useApiErrorHandler({
    error: updateGraphQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to update graph query template, please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);
    },
  });

  // ----------------------------------------------------------------------------------------
  // Restore graph template.
  // ----------------------------------------------------------------------------------------
  const { error: restoreGraphQueryTemplateError, callback: handleRestoreGraphQueryTemplate } =
    useApiCallback(async (graphQueryTemplateId: GraphQueryTemplateId) => {
      restoreGraphQueryTemplate(graphQueryTemplateId);
    }, []);

  useApiErrorHandler({
    error: restoreGraphQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to restore graph query template, please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // ----------------------------------------------------------------------------------------
  // Archive graph template.
  // ----------------------------------------------------------------------------------------
  const { error: archiveGraphQueryTemplateError, callback: handleArchiveGraphQueryTemplate } =
    useApiCallback(async (graphQueryTemplateId: GraphQueryTemplateId) => {
      archiveGraphQueryTemplate(graphQueryTemplateId);
    }, []);

  useApiErrorHandler({
    error: archiveGraphQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to archive graph query template, please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const handleFiltersStatusUpdate = (statuses: string[]) => {
    setFilters({
      ...filters,
      status: statuses.map((status) => status as GraphQueryTemplateStatus),
    });
  };

  return (
    <DashboardGraphQueryTemplatesView
      archiveGraphQueryTemplate={handleArchiveGraphQueryTemplate}
      filters={filters}
      graphQueryTemplates={graphQueryTemplates}
      handleFiltersStatusUpdate={handleFiltersStatusUpdate}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
      isLoadingGraphQueryTemplates={_loadingTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={paginationParams}
      restoreGraphQueryTemplate={handleRestoreGraphQueryTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={sortParams}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.graphQueryTemplates.currentPage,
    filters: state.graphQueryTemplates.filters,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    pageSize: state.graphQueryTemplates.pageSize,
    paginationParams: state.graphQueryTemplates.paginationParams,
    sortParams: state.graphQueryTemplates.sortParams,
  };
};

const mapDispatchToProps = {
  archiveGraphQueryTemplate,
  loadGraphQueryTemplates,
  restoreGraphQueryTemplate,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardGraphQueryTemplatesRoute);
